import { AION_INGEST_RELATED_PARTIES_DATA } from '../RelatedParties/relatedParty_constants';

// Filter source key for this tab's DataTable — the redux slice / filterSourceKey both
// key their per-view filter state off it.
export const MANAGEMENT_PROFILE_FILTER_SOURCE_KEY = 'MANAGEMENT_PROFILE';

// Wire fields whose cells are rendered by a bespoke COMPONENT renderer (see
// managementProfileColumnRenderers). Keyed switch below in that file maps each to its cell.
export const MANAGEMENT_PROFILE_COMPONENT_FIELDS = {
    NAME: 'name',
    TENURE: 'tenure',
    CONTACT: 'contact',
    SHAREHOLDING: 'shareholdingPercentage',
    DESIGNATION: 'designation',
    RISK_INDICATOR_COUNT: 'riskIndicatorCount',
    AML_PEP_RISK_COUNT: 'amlPepRiskCount',
    LEGAL_CASES_COUNT: 'legalCasesCount',
    LEGAL_CASES_FILED_BY: 'legalCasesFiledBy',
    CREDIT_SCORE: 'creditScore',
} as const;

export type ColumnLockKey = 'riskAlerts' | 'regulatoryChecks' | 'legalCases';

export const LOCKED_COLUMN_TOOLTIPS: Record<ColumnLockKey, string> = {
    riskAlerts: 'Unlock Structure & Relations to view risk alerts',
    regulatoryChecks: 'Unlock Legal & Risk Checks to view regulatory checks',
    legalCases: 'Unlock Legal & Risk Checks to view legal cases',
};

export const NOT_CONFIGURED_LABEL = 'Not Configured';
export const NOT_CONFIGURED_TOOLTIP = 'Your AICA Configuration does not run checks for this Director Designation or Director Status';

// Risk Alerts states sent by polus (riskAlertsState); No Data and Not Checked reuse the regulatory words.
export const RISK_ALERTS_STATE = {
    ALERTS: 'ALERTS',
    NO_ALERTS: 'NO_ALERTS',
    NOT_AVAILABLE: 'NOT_AVAILABLE',
    NOT_CHECKED: 'NOT_CHECKED',
} as const;

export const RISK_ALERTS_TOOLTIPS: Record<(typeof RISK_ALERTS_STATE)[keyof typeof RISK_ALERTS_STATE], string> = {
    [RISK_ALERTS_STATE.ALERTS]: 'Click to view MCA alerts',
    [RISK_ALERTS_STATE.NO_ALERTS]: 'No MCA alerts',
    [RISK_ALERTS_STATE.NOT_AVAILABLE]: 'Not able to fetch data for MCA alerts',
    [RISK_ALERTS_STATE.NOT_CHECKED]: 'MCA Risk alerts are run only on MCA Registered Director',
};

// Task steps the Management Profile request asks for: polus needs the related-party ingest status to
// tell Risk Alerts' No Data (a failed fetch) from a fetch that is still running.
export const MANAGEMENT_PROFILE_TASK_STEPS = [AION_INGEST_RELATED_PARTIES_DATA];

// Copy shared by the Management Profile cells and the director drawer rows.
export const ALERT_UNITS = { SINGULAR: 'Alert', PLURAL: 'Alerts' } as const;
export const INDICATOR_UNITS = { SINGULAR: 'Indicator', PLURAL: 'Indicators' } as const;
export const NO_ALERTS_LABEL = 'No Alerts';
export const NO_ALERTS_DRAWER_LABEL = 'No alerts found';
export const DRAWER_ROW_LABELS = {
    CREDIT_BUREAU: 'Credit Bureau',
    RISK_ALERTS: 'Risk Alerts',
    REGULATORY_CHECKS: 'Regulatory Checks',
} as const;

// Maps each lockable base column's wire field to its lock key, so the header decoration
// (headerIcon/showHeaderIcon/headerIconTooltip, D9) can be applied by field to the
// polus-config-sourced columns (the only column source — there is no FE fallback).
export const LOCKABLE_COLUMN_FIELD_TO_LOCK_KEY: Partial<Record<string, ColumnLockKey>> = {
    riskIndicatorCount: 'riskAlerts',
    amlPepRiskCount: 'regulatoryChecks',
    legalCasesCount: 'legalCases',
    legalCasesFiledBy: 'legalCases',
};

// Key used in headerIconMap (passed to DataTable) for the lock padlock icon.
export const LOCK_HEADER_ICON_KEY = 'LOCK';

export const KARZA_DIN_STEP = 'AION_KARZA_DIN_DETAILS';

export const DIN_FETCH_STEP = 'AION_FETCH_DIN_DETAILS';

// Set when the base-details ingest (the auto-populate-at-lead-addition path) completes — the
// step that unlocks Management Profile ahead of any DIN fetch. Drives the first-run banner (D7).
export const BASE_DETAILS_INGEST_STEP = 'AION_INGEST_BASE_DETAILS';

export const PROBE_MERGE_WAIT_MS = 30_000;

// First-run banner copy (D7 / §4c conflict A). Deliberately generic: the DIN fetch this CTA
// triggers only populates director identity/contact fields — risk, AML/PEP, legal-case, and
// bureau data come from separate flows, so naming them here would overclaim.
export const FETCH_DETAILS_BANNER_COPY = {
    BODY: 'Refresh to enrich the Management Profile with detailed information.',
    CTA_LABEL: 'Refresh Now',
} as const;

/**
 * URL flag the Management Profile tab publishes while the Director Details drawer is open, so
 * AskAicaV2 hides its floating icon (see isAnyDrawerOpen). Shared so the two sides can't drift.
 */
export const DIRECTOR_DRAWER_PARAM = 'directorDrawer';

/**
 * The roster's deep-link handle for useFocusRowFromUrlParam: the URL param the tab sets to focus
 * one director, and the matching `data-` attribute its rows carry on both surfaces. Both sides go
 * through `normalizeNameKey` — the hook normalises the param, so the row must be normalised too.
 */
export const FOCUS_DIRECTOR_PARAM = 'focusDirectorId';
export const DIRECTOR_ROW_DATA_ATTR = 'mp-directorid';
